import { useState, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { UserPlus, Edit, Trash2, Loader2, Headphones, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminOpsManagersTab({ opsManagers, isLoading, onRefresh }) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingOps, setEditingOps] = useState(null);
  const editingOpsRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    user_id: null, email: "", name: "", username: "", password: "", phone: ""
  });

  const openAddDialog = () => {
    editingOpsRef.current = null;
    setEditingOps(null);
    setFormData({ user_id: null, email: "", name: "", username: "", password: "", phone: "" });
    setIsDialogOpen(true);
  };

  const openEditDialog = (e) => {
    // Get user_id from the button's data attribute to avoid closure issues
    const userId = e.currentTarget.getAttribute('data-user-id');
    const ops = opsManagers.find(o => o.user_id === userId);
    if (!ops) {
      toast.error("Could not find ops manager");
      return;
    }
    
    editingOpsRef.current = userId;
    setEditingOps(userId);
    setFormData({
      user_id: userId,
      email: ops.email || "",
      name: ops.name || "",
      username: ops.username || "",
      password: "",
      phone: ops.phone || ""
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.email || !formData.name || !formData.username) {
      toast.error("Email, name, and username are required");
      return;
    }
    // Get user_id from form data (most reliable source)
    const userIdToUpdate = formData.user_id;
    if (!userIdToUpdate && !formData.password) {
      toast.error("Password is required for new operations manager");
      return;
    }
    setIsSubmitting(true);
    
    try {
      if (userIdToUpdate) {
        const updateData = {
          email: formData.email,
          name: formData.name,
          username: formData.username,
          phone: formData.phone
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await axios.put(`${API}/admin/ops-managers/${userIdToUpdate}`, updateData);
        toast.success("Operations manager updated");
      } else {
        await axios.post(`${API}/admin/ops-managers`, {
          email: formData.email,
          name: formData.name,
          username: formData.username,
          password: formData.password,
          phone: formData.phone
        });
        toast.success("Operations manager created");
      }
      setIsDialogOpen(false);
      editingOpsRef.current = null;
      setEditingOps(null);
      setFormData({ user_id: null, email: "", name: "", username: "", password: "", phone: "" });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save operations manager");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Deactivate this operations manager?")) return;
    try {
      await axios.delete(`${API}/admin/ops-managers/${userId}`);
      toast.success("Operations manager deactivated");
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to deactivate");
    }
  };

  const handleReactivate = async (userId) => {
    try {
      await axios.post(`${API}/admin/ops-managers/${userId}/reactivate`);
      toast.success("Operations manager reactivated");
      onRefresh();
    } catch (error) {
      toast.error("Failed to reactivate");
    }
  };

  return (
    <>
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6 mb-6">
        <Card className="border-0 shadow-card">
          <CardContent className="p-4 lg:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Ops Managers</p>
                <p className="text-2xl lg:text-3xl font-heading mt-1">{opsManagers.length}</p>
              </div>
              <div className="w-10 h-10 lg:w-12 lg:h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <Headphones className="w-5 h-5 lg:w-6 lg:h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-card">
          <CardContent className="p-4 lg:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl lg:text-3xl font-heading mt-1 text-green-600">
                  {opsManagers.filter(o => o.is_active).length}
                </p>
              </div>
              <div className="w-10 h-10 lg:w-12 lg:h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <Headphones className="w-5 h-5 lg:w-6 lg:h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-card">
          <CardContent className="p-4 lg:p-6 flex items-center justify-center">
            <Button onClick={openAddDialog} className="rounded-full" data-testid="add-ops-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Add Ops Manager
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="border-0 shadow-card overflow-hidden" data-testid="ops-managers-table-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg lg:text-xl">Operations Managers</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0 sm:p-6">
          {isLoading ? (
            <div className="flex justify-center py-8"><div className="spinner"></div></div>
          ) : opsManagers.length === 0 ? (
            <div className="text-center py-8">
              <Headphones className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No operations managers yet</p>
            </div>
          ) : (
            <div className="w-full overflow-x-auto">
              <Table className="min-w-[600px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Username</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {opsManagers.map((ops) => (
                    <TableRow key={ops.user_id} data-testid={`ops-row-${ops.user_id}`}>
                      <TableCell>
                        <p className="font-semibold text-sm">{ops.name}</p>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-xs sm:text-sm truncate max-w-[120px] sm:max-w-[180px]">{ops.email}</p>
                          <p className="text-xs text-muted-foreground">{ops.phone || "No phone"}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-muted px-2 py-1 rounded">{ops.username}</code>
                      </TableCell>
                      <TableCell>
                        <Badge className={`rounded-full text-xs ${ops.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}`}>
                          {ops.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {ops.is_active ? (
                          <>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              data-user-id={ops.user_id}
                              onClick={openEditDialog}
                              data-testid={`edit-ops-${ops.user_id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="text-destructive" 
                              onClick={() => handleDelete(ops.user_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        ) : (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleReactivate(ops.user_id)}
                            className="text-green-600"
                          >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Reactivate
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => {
        setIsDialogOpen(open);
        if (!open) {
          editingOpsRef.current = null;
          setEditingOps(null);
          setFormData({ user_id: null, email: "", name: "", username: "", password: "", phone: "" });
        }
      }}>
        <DialogContent className="max-w-md" data-testid="ops-manager-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingOps ? "Edit Operations Manager" : "Add Operations Manager"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input
                data-testid="ops-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                data-testid="ops-email-input"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Username *</Label>
              <Input
                data-testid="ops-username-input"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder="johndoe"
              />
            </div>
            <div className="space-y-2">
              <Label>{editingOps ? "New Password (leave empty to keep current)" : "Password *"}</Label>
              <Input
                data-testid="ops-password-input"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone Number</Label>
              <Input
                data-testid="ops-phone-input"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+91 9876543210"
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => {
              setIsDialogOpen(false);
              editingOpsRef.current = null;
              setEditingOps(null);
              setFormData({ user_id: null, email: "", name: "", username: "", password: "", phone: "" });
            }} className="rounded-full">Cancel</Button>
            <Button onClick={handleSubmit} disabled={isSubmitting} className="rounded-full" data-testid="save-ops-btn">
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingOps ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
